import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from "lucide-react";

export type AlertType = "success" | "error" | "warning" | "info";

export interface AlertPayload {
  id: number;
  type: AlertType;
  title?: string;
  message?: string;
  duration?: number; // ms; 0 or negative = sticky
}

interface Props {
  alert: AlertPayload | null;
  onDismiss: () => void;
}

const TYPE_CONFIG: Record<
  AlertType,
  {
    accent: string;       // ring + bar + icon bg
    accentText: string;   // title color
    tint: string;         // card background
    border: string;       // card border
    Icon: React.ComponentType<{ className?: string }>;
  }
> = {
  success: {
    accent: "bg-emerald-500",
    accentText: "text-emerald-700",
    tint: "bg-emerald-50",
    border: "border-emerald-200",
    Icon: CheckCircle2,
  },
  error: {
    accent: "bg-red-500",
    accentText: "text-red-700",
    tint: "bg-red-50",
    border: "border-red-200",
    Icon: AlertCircle,
  },
  warning: {
    accent: "bg-amber-500",
    accentText: "text-amber-700",
    tint: "bg-amber-50",
    border: "border-amber-200",
    Icon: AlertTriangle,
  },
  info: {
    accent: "bg-blue-500",
    accentText: "text-blue-700",
    tint: "bg-blue-50",
    border: "border-blue-200",
    Icon: Info,
  },
};

/**
 * TopAlert — slides down from the top of the viewport, auto-dismisses.
 *
 * Visually matches the mobile app's TopAlert: accent bar on the left,
 * white-circle icon, title + message, close button. One alert at a time.
 *
 * Render in a layout root — usually inside <AlertProvider>.
 */
const TopAlert: React.FC<Props> = ({ alert, onDismiss }) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Schedule auto-dismiss whenever a new alert appears.
  useEffect(() => {
    if (!alert) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    const duration = alert.duration ?? 3500;
    if (duration > 0) {
      timerRef.current = setTimeout(onDismiss, duration);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [alert?.id, onDismiss]);

  // Pause auto-dismiss on hover, resume on leave.
  const onMouseEnter = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
  const onMouseLeave = () => {
    if (!alert) return;
    const duration = alert.duration ?? 3500;
    if (duration > 0) timerRef.current = setTimeout(onDismiss, duration);
  };

  const cfg = alert ? TYPE_CONFIG[alert.type] : TYPE_CONFIG.info;
  const Icon = cfg.Icon;

  return (
    <div
      className="fixed top-4 right-4 sm:top-5 sm:right-5 z-[9999] pointer-events-none max-w-[calc(100vw-2rem)]"
      aria-live="polite"
      aria-atomic="true"
    >
      <AnimatePresence mode="wait">
        {alert && (
          <motion.div
            key={alert.id}
            initial={{ x: 360, opacity: 0, scale: 0.96 }}
            animate={{ x: 0, opacity: 1, scale: 1 }}
            exit={{ x: 360, opacity: 0, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 380, damping: 30, mass: 0.7 }}
            className={`relative w-80 ${cfg.tint} ${cfg.border} border rounded-lg shadow-lg shadow-black/10 overflow-hidden pointer-events-auto`}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            role={alert.type === "error" || alert.type === "warning" ? "alert" : "status"}
          >
            {/* Left accent bar */}
            <div className={`absolute left-0 top-0 bottom-0 w-1 ${cfg.accent}`} />

            <div className="flex items-start gap-2.5 pl-3.5 pr-1.5 py-2.5">
              {/* Icon circle */}
              <div className={`shrink-0 w-7 h-7 rounded-full ${cfg.accent} flex items-center justify-center shadow-sm mt-0.5`}>
                <Icon className="w-4 h-4 text-white" />
              </div>

              {/* Text — wraps fully, never truncates */}
              <div className="flex-1 min-w-0">
                {alert.title && (
                  <p className={`text-xs font-bold leading-tight tracking-tight break-words ${cfg.accentText}`}>
                    {alert.title}
                  </p>
                )}
                {alert.message && (
                  <p className="text-xs text-gray-700 leading-snug mt-0.5 break-words whitespace-pre-line">
                    {alert.message}
                  </p>
                )}
              </div>

              {/* Close */}
              <button
                type="button"
                onClick={onDismiss}
                aria-label="Dismiss"
                className="shrink-0 w-6 h-6 rounded-full bg-white/70 hover:bg-white text-gray-500 hover:text-gray-700 flex items-center justify-center transition-colors border border-white/80 mt-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </div>

            {/* Auto-dismiss progress bar (only when duration > 0) */}
            {(alert.duration ?? 3500) > 0 && (
              <motion.div
                key={`bar-${alert.id}`}
                initial={{ scaleX: 1 }}
                animate={{ scaleX: 0 }}
                transition={{ duration: (alert.duration ?? 3500) / 1000, ease: "linear" }}
                style={{ transformOrigin: "left" }}
                className={`h-0.5 ${cfg.accent} opacity-60`}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TopAlert;
